import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { motion, useInView, animate } from "framer-motion";

const AnimatedCounter = ({ value, suffix = "" }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  useEffect(() => {
    if (isInView) {
      // Find numeric part
      const numValue = parseInt(value, 10);
      if (isNaN(numValue)) {
        setDisplayValue(value);
        return;
      }

      const controls = animate(0, numValue, {
        duration: 2,
        ease: "easeOut",
        onUpdate: (latest) => {
          setDisplayValue(Math.floor(latest));
        },
      });

      return () => controls.stop();
    }
  }, [isInView, value]);

  return (
    <span ref={ref} className="tabular-nums">
      {displayValue}
      {suffix}
    </span>
  );
};

const CompanyStats = () => {
  const { t } = useTranslation();
  
  const stats = [
    { value: 5, suffix: "+", label: "yearsInBusiness" },
    { value: 100, suffix: "+", label: "solutionsDelivered" },
    { value: 95, suffix: "%", label: "customerRetention" },
    { value: 20, suffix: "+", label: "teamMembersLabel" }, // We will add a key or default
  ];

  return (
    <section className="py-20 md:py-28 bg-slate-50 dark:bg-[#0c0c0e] transition-colors duration-500 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white mt-2 tracking-tight"
          >
            {t('statistics_title', 'Softkel in Numbers')}
          </motion.h2>
          <motion.div 
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="w-20 h-1 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-purple-500 dark:to-pink-500 mx-auto mt-4 rounded-full origin-center"
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, type: "spring", stiffness: 100 }}
              className="flex flex-col items-center justify-center p-8 rounded-3xl bg-white dark:bg-[#111111] border border-slate-200/50 dark:border-neutral-800/40 shadow-sm dark:shadow-2xl hover:shadow-lg dark:hover:shadow-purple-500/5 transition-all duration-300 group hover:-translate-y-1"
            >
              <div className="text-4xl md:text-5xl font-black bg-clip-text text-transparent bg-gradient-to-br from-blue-600 to-indigo-600 dark:from-purple-400 dark:to-pink-500 mb-2">
                <AnimatedCounter value={stat.value} suffix={stat.suffix} />
              </div>
              <div className="text-xs sm:text-sm font-semibold text-slate-500 dark:text-neutral-400 uppercase tracking-widest text-center group-hover:text-blue-600 dark:group-hover:text-purple-400 transition-colors">
                {t(stat.label, stat.label === "teamMembersLabel" ? "Team Members" : stat.label)}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CompanyStats;
